import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';

const API_BASE_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://food.api.j7lab.com';

const BLOG_API_URL =
  process.env.REACT_APP_BLOG_API_URL ||
  `${API_BASE_URL}/api/public/blog-posts`;

const normalizePosts = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.posts)) return payload.posts;
  if (Array.isArray(payload.data?.posts)) return payload.data.posts;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const pickCoverImage = (post) => {
  if (!post) return null;
  return (
    post.coverImage ||
    post.image ||
    post.imageUrl ||
    post.heroImage ||
    null
  );
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const fetchPosts = async () => {
      setStatus('loading');
      setErrorMessage(null);

      try {
        const response = await fetch(BLOG_API_URL, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const normalized = normalizePosts(payload);

        if (!isActive) return;

        setPosts(normalized);
        setStatus('success');
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Erreur lors du chargement des articles de blog:', error);
        if (!isActive) return;
        setPosts([]);
        setErrorMessage('Aucun article disponible pour le moment.');
        setStatus('error');
      }
    };

    fetchPosts();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  return (
    <>
      <Seo
        title="Blog"
        description="Conseils, inspirations et actualités pour mieux planifier vos repas et cuisiner avec YAQAM."
        breadcrumb
      />
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#2C3E50' }}>Le blog YAQAM</h1>
          <p className="text-base md:text-lg" style={{ color: '#6c757d' }}>
            Retrouvez nos conseils, actualités et inspirations pour cuisiner sereinement.
          </p>
        </header>

        {status === 'loading' && (
          <div className="flex justify-center py-20">
            <div className="h-12 w-12 rounded-full border-4 border-orange-200 border-t-[#E67E22] animate-spin"></div>
          </div>
        )}

        {status === 'error' && (
          <div className="max-w-md mx-auto bg-white border border-orange-200 rounded-2xl shadow-sm p-6 text-center" style={{ color: '#6c757d' }}>
            {errorMessage}
          </div>
        )}

        {status !== 'loading' && posts.length === 0 && !errorMessage && (
          <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm p-6 text-center" style={{ color: '#6c757d' }}>
            Aucun article n'est disponible pour le moment. Revenez bientôt !
          </div>
        )}

        {posts.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, index) => {
              const cover = pickCoverImage(post);
              const publishedAt = formatDate(post.publishedAt || post.createdAt || post.date);
              const excerpt =
                post.excerpt ||
                post.summary ||
                post.description ||
                (Array.isArray(post.paragraphs) ? post.paragraphs[0] : '') ||
                '';
              const title = post.title || post.name || `Article ${index + 1}`;
              const targetUrl = post.url || post.href || null;

              return (
                <article
                  key={post.id || post.slug || post.url || index}
                  className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col"
                >
                  {cover ? (
                    <div className="relative h-48">
                      <img src={cover} alt={title} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-orange-100 via-yellow-100 to-white flex items-center justify-center text-4xl">
                      🥗
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    {publishedAt && (
                      <span className="text-xs uppercase tracking-wide mb-2" style={{ color: '#E67E22' }}>
                        {publishedAt}
                      </span>
                    )}
                    <h2 className="text-lg font-semibold mb-3" style={{ color: '#2C3E50' }}>{title}</h2>
                    <p className="text-sm flex-1" style={{ color: '#6c757d' }}>
                      {excerpt.length > 160 ? `${excerpt.slice(0, 157)}...` : excerpt}
                    </p>
                    {targetUrl ? (
                      <a
                        href={targetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold"
                        style={{ color: '#E67E22' }}
                      >
                        Lire l'article
                        <span aria-hidden="true">→</span>
                      </a>
                    ) : (
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gray-400">
                        Lecture bientôt disponible
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default BlogPage;
